import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, errorMessage } from '../api.js'
import { optimistic } from '../optimistic.js'
import { useToast } from '../../components/ui/Toast.jsx'
import { qk } from './keys.js'

export function useSettings({ enabled = true } = {}) {
  return useQuery({
    queryKey: qk.settings,
    queryFn: ({ signal }) => api('/settings', { signal }),
    enabled,
  })
}

export function useSaveAiSettings() {
  const queryClient = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: (ai) => api('/settings/ai', { method: 'PUT', body: ai }),
    onMutate: async (ai) => {
      const rollback = await optimistic(queryClient, { queryKey: qk.settings }, (data) => ({ ...data, ai }))
      return { rollback }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(qk.settings, data)
      queryClient.invalidateQueries({ queryKey: qk.me })
      toast.success('AI settings saved')
    },
    onError: (error, _ai, context) => {
      context?.rollback()
      toast.error(`Settings not saved — ${errorMessage(error)}`)
    },
  })
}

export function useUsers({ enabled = true } = {}) {
  return useQuery({
    queryKey: qk.users,
    queryFn: ({ signal }) => api('/users', { signal }).then((d) => d.users),
    enabled,
  })
}

export function useSetUserRole() {
  const queryClient = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: ({ id, role }) => api(`/users/${id}`, { method: 'PATCH', body: { role } }),
    onMutate: async ({ id, role }) => {
      const rollback = await optimistic(queryClient, { queryKey: qk.users }, (users) =>
        users.map((u) => (u.id === id ? { ...u, role } : u)),
      )
      return { rollback }
    },
    onError: (error, _vars, context) => {
      context?.rollback()
      toast.error(`Role not changed — ${errorMessage(error)}`)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: qk.users }),
  })
}
